import { BaseWebComponentView } from '../../../framework/BaseWebComponentView';
import { BreachPathDetailsWebComponentSchema, BreachPathIssue, BreachPathNode } from '../types';
import { GzSideMenuWebComponent } from 'apps/webcomponents/src';
import { BreachPathDetailsGridDataProvider } from '../dataProvider/BreachPathDetailsGridDataProvider'
import { ColumnDetails, ColumnType } from 'apps/webcomponents/src/grid/types/common';
import { Column, ColumnKeys } from '../types';
import { DefaultGridColumnFixedWidths } from '@nirvana/shared/dtos/types/shared/grid';
import { translateFn } from 'apps/webcomponents/src/utils/translator.helper';
import {
  resourceNameRenderer,
  resourceIdRenderer,
  resourceTypeRenderer,
  platformRenderer,
} from './customGridRowRenderers';
import {
  setGridCommonProperties,
} from 'apps/webcomponents/src/riskManagement/common/utils';
import { ViewType } from 'apps/webcomponents/src/grid/gridView/types';
import { NavigationHelper } from '@nirvana/shared/navigation';
import { breachPathIssueNameMapping } from '../../common/utils';
import { BreachPathIssueNameKey } from '../../common/types';
import { BreachPathEvents } from '../../common/types';

export class BreachPathDetailsView extends BaseWebComponentView {
  public breachPathIssueId: string;
  public tabName: string;
  public breachPathIssue: BreachPathIssue | null = null;

  getDomElements() {
    this.dom = {
      $page: document.querySelector('gz-breach-path-details'),
      $tabGroup: this.shadow.querySelector('#tab-group'),
      $graphTab: this.shadow.querySelector('#graph-tab'),
      $resourcesTab: this.shadow.querySelector('#resources-tab'),
      $sideMenu: this.shadow.querySelector('#detailsSideMenu') as GzSideMenuWebComponent,
      $metadataSideMenu: this.shadow.querySelector('#metadataSideMenu') as GzSideMenuWebComponent,
      $graphSlot: this.shadow.querySelector('slot[name="issue-graph-extjs-slot"]'),
      $mainGrid: this.shadow.querySelector('#main-resource-grid'),
      $otherGrid: this.shadow.querySelector('#other-resource-grid'),
      $mainGridTitle: this.shadow.querySelector('#main-grid-title'),
      $otherGridTitle: this.shadow.querySelector('#other-grid-title'),
      $gridLoader: this.shadow.querySelector('.grid-loader'),
      $backButton: this.shadow.querySelector('#back-button'),
      $backButtonText: this.shadow.querySelector('#back-button-text'),
      $pageNavigationHeader: this.shadow.querySelector('gz-page-navigation-header'),
    }
  }

  async buildSuccessView(data: BreachPathDetailsWebComponentSchema) {
    // Set translated strings
    this.setTranslatedStrings();

    // Hide sidebar for P0
    const pushBodySidenav = this.shadow.querySelector('gz-push-body-layout').shadowRoot.querySelector('#pushBodySidenav');
    pushBodySidenav.style.display = 'none';

    // Inject route to `gz-tab`
    this.dom.$graphTab.route = `/risk-management-breach-path/graph/${this.breachPathIssueId}`;
    this.dom.$resourcesTab.route = `/risk-management-breach-path/resources/${this.breachPathIssueId}`;

    // Navigate based on route
    if (this.tabName === 'graph') {
      this.dom.$tabGroup.selectTab(0);
    } else if (this.tabName === 'resources') {
      this.dom.$tabGroup.selectTab(1);
    }

    // Retrieve Breach Path Issue data
    if (!this.breachPathIssue) {
      const breachPathIssueResponse =
        await this.dependencies.api.clients.BreachPathIssues.getBreachPathIssueDataById({ issueId: this.breachPathIssueId }).toPromise();
      this.breachPathIssue = breachPathIssueResponse.data;
    }

    // Populate page navigation header
    this.dom.$pageNavigationHeader.items = [
      {
        text: 'Breach Path',
        onClickFunction: () => {
          NavigationHelper.navigateTo(`/risk-management-breach-path`);
        }
      },
      {
        text: breachPathIssueNameMapping[(this.breachPathIssue as BreachPathIssue).name] ?? breachPathIssueNameMapping[BreachPathIssueNameKey.Unknown],
      },
    ]

    // Set up back button onclick event
    this.dom.$backButton.onclick = () => {
      this.dom.$backButton.dispatchEvent(
        new CustomEvent(BreachPathEvents.GO_BACK_DETAILS_PAGE, {
          bubbles: true,
          cancelable: true,
          composed: true,
        })
      );
    };
  }

  setTranslatedStrings(): void {
    this.dom.$graphTab.label = translateFn('Graph');
    this.dom.$resourcesTab.label = translateFn('Resources');
    this.dom.$mainGridTitle.innerHTML = translateFn('Main resource');
    this.dom.$otherGridTitle.innerHTML = translateFn('Other involved resources');
    this.dom.$backButtonText.innerHTML = translateFn('Back');
  }

  async buildGrid(grid, data: BreachPathNode[]) {
    if (grid.gridLoaded) {
      return;
    }

    grid.propertiesSet = false;

    const view = {
      type: ViewType.DEFAULT,
      filters: [],
      displayedColumns: [
        Column.RESOURCE_NAME,
        Column.RESOURCE_ID,
        Column.RESOURCE_TYPE,
        Column.PLATFORM,
      ],
      pageSize: 500,
      selectedTargets: [],
      compactView: false,
    };

    setGridCommonProperties(grid, view, false, view);

    grid.dataProvider = new BreachPathDetailsGridDataProvider(this.dependencies, data);
    grid.allColumnDetails = this.getGridColumns();
    grid.columnRenderers = {
      [ColumnKeys.RESOURCE_NAME]: resourceNameRenderer.bind(this),
      [ColumnKeys.RESOURCE_ID]: resourceIdRenderer.bind(this),
      [ColumnKeys.RESOURCE_TYPE]: resourceTypeRenderer.bind(this),
      [ColumnKeys.PLATFORM]: platformRenderer.bind(this),
    };
    grid.columns = [
      Column.RESOURCE_NAME,
      Column.RESOURCE_ID,
      Column.RESOURCE_TYPE,
      Column.PLATFORM,
    ];
    grid.enableRowClickEvent = true;
    grid.hasSelectableRows = false;
    grid.enableCSVExport = false;
    grid.customStrings = {
      noSearchResultsString: this.dependencies.translateFn('No resources found'),
    };

    grid.propertiesSet = true;

    if (!grid.started) {
      grid.injectDependencies(this.dependencies);
      await grid.start();
    }

    // Remove loading animation
    grid.style.display = 'block';
    grid.previousElementSibling.style.display = 'none';
  }

  protected getGridColumns(): Array<ColumnDetails<Column, ColumnKeys>> {
    const columns: Array<ColumnDetails<Column, ColumnKeys>> = [
      {
        column: Column.RESOURCE_NAME,
        key: ColumnKeys.RESOURCE_NAME,
        columnName: translateFn('Resource name'),
        type: ColumnType.STRING,
        width: DefaultGridColumnFixedWidths.M,
        removable: false,
        sortable: false,
        filteringDisabled: true,
      },
      {
        column: Column.RESOURCE_ID,
        key: ColumnKeys.RESOURCE_ID,
        columnName: translateFn('Resource ID'),
        type: ColumnType.STRING,
        width: DefaultGridColumnFixedWidths.L,
        removable: false,
        sortable: false,
        filteringDisabled: true,
      },
      {
        column: Column.RESOURCE_TYPE,
        key: ColumnKeys.RESOURCE_TYPE,
        columnName: translateFn('Resource type'),
        type: ColumnType.STRING,
        width: DefaultGridColumnFixedWidths.S,
        removable: false,
        sortable: false,
        filteringDisabled: true,
      },
      {
        column: Column.PLATFORM,
        key: ColumnKeys.PLATFORM,
        columnName: translateFn('Platform'),
        type: ColumnType.STRING,
        removable: false,
        sortable: false,
        filteringDisabled: true,
      },
    ];

    return columns;
  }
}
